import type { AxiosInstance } from "axios";
import { dayjs } from "@/core/utils/date";
import type {
  IAiScriptExplanationChatResponse,
  IAiScriptExplanationChatStreamResponse,
  IAiScriptExplanationChatThread,
  IAiScriptExplanationChatTurnResponse,
} from "./types";

const TURN_POLL_INTERVAL_MS = 1500;
const TURN_TIMEOUT_MS = 5 * 60 * 1000;

const sleep = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

interface RunAiScriptExplanationChatTurnParams {
  api: AxiosInstance;
  chatPath: string;
  message: string;
  threadId?: string | null;
  threadName?: string | null;
  // Fires as soon as the turn is accepted, with the (created or reused) thread.
  onThread?: (thread: IAiScriptExplanationChatThread, turnId: string) => void;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

export interface AiScriptExplanationChatTurnResult {
  response: IAiScriptExplanationChatResponse;
  thread: IAiScriptExplanationChatThread;
  turnId: string;
}

interface FollowAiScriptExplanationChatTurnParams {
  api: AxiosInstance;
  chatPath: string;
  turnId: string;
  onTextDelta?: (delta: string) => void;
  onProgress?: (label: string) => void;
}

export const followAiScriptExplanationChatTurn = async ({
  api,
  chatPath,
  turnId,
  onTextDelta,
  onProgress,
}: FollowAiScriptExplanationChatTurnParams): Promise<IAiScriptExplanationChatResponse> => {
  const startedAt = dayjs();
  let cursor = 0;

  for (;;) {
    const { data: stream } =
      await api.get<IAiScriptExplanationChatStreamResponse>(
        `${chatPath}/stream`,
        {
          params: { turn_id: turnId, since: cursor },
        },
      );
    cursor = stream.cursor ?? cursor;

    for (const event of stream.events ?? []) {
      if (event.kind === "text_delta" && event.content) {
        onTextDelta?.(event.content);
      }
      if (event.kind === "progress" && event.content) {
        onProgress?.(event.content);
      }
      if (event.kind === "event_finished" && event.content) {
        try {
          return JSON.parse(event.content) as IAiScriptExplanationChatResponse;
        } catch {
          const message = "Received malformed final response from chat stream.";
          return {
            message,
            sources: [],
            run_context: null,
            error: {
              code: "malformed_stream_response",
              message,
            },
          };
        }
      }
      if (event.kind === "error") {
        throw new Error(
          event.content ?? "Sorry, an error occurred. Please try again.",
        );
      }
    }

    if (dayjs().diff(startedAt, "milliseconds") > TURN_TIMEOUT_MS) {
      throw new Error("Timed out waiting for the answer. Please try again.");
    }

    await sleep(TURN_POLL_INTERVAL_MS);
  }
};

export const runAiScriptExplanationChatTurn = async ({
  api,
  chatPath,
  message,
  threadId,
  threadName,
  onThread,
  onTextDelta,
  onProgress,
}: RunAiScriptExplanationChatTurnParams): Promise<AiScriptExplanationChatTurnResult> => {
  const { data: created } =
    await api.post<IAiScriptExplanationChatTurnResponse>(chatPath, {
      message,
      thread_id: threadId ?? undefined,
      thread_name: threadName ?? undefined,
    });
  const { turn_id: turnId, thread } = created;
  onThread?.(thread, turnId);
  const response = await followAiScriptExplanationChatTurn({
    api,
    chatPath,
    turnId,
    onTextDelta,
    onProgress,
  });

  return {
    response,
    thread,
    turnId,
  };
};
